import { isNotDefined, getSourcePathAccessor } from "../utils";
import { EMA as defaultOptions } from "./defaultOptionsForComputation";

/**
 * SMMA - Smoothed Moving Average (Welles Wilder's Smoothing)
 *
 * Also known as Wilder's Moving Average or RMA (Running Moving Average).
 * Developed by J. Welles Wilder Jr. for use in his indicators (RSI, ADX, ATR).
 *
 * Formula:
 * SMMA uses a modified exponential smoothing with alpha = 1/N (vs EMA's 2/(N+1))
 *
 * SMMA[i] = (SMMA[i-1] * (N - 1) + Price[i]) / N
 *         = SMMA[i-1] + (Price[i] - SMMA[i-1]) / N
 *
 * Or in EMA form:
 * SMMA[i] = alpha * Price[i] + (1 - alpha) * SMMA[i-1]
 * where alpha = 1/N
 *
 * Characteristics:
 * - Smoother than EMA (smaller alpha)
 * - Slower to respond to price changes
 * - First value initialized with SMA
 *
 * @example
 * For N=14:
 * - EMA alpha = 2/15 ≈ 0.133
 * - SMMA alpha = 1/14 ≈ 0.071 (about half of EMA)
 */

export default function () {
  let options = defaultOptions;

  function calculator(data) {
    const { windowSize, sourcePath } = options;
    const source = getSourcePathAccessor(sourcePath);

    // Welles Wilder smoothing factor (different from standard EMA)
    const alpha = 1 / windowSize;

    let previous;
    let initialAccumulator = 0;
    let skip = 0;

    return data.map(function (d, i) {
      const v = source(d, i);

      // Skip undefined values at start
      if (isNotDefined(previous) && isNotDefined(v)) {
        skip++;
        return undefined;
      } else if (i < windowSize + skip - 1) {
        // Accumulate for initial SMA
        initialAccumulator += v;
        return undefined;
      } else if (i === windowSize + skip - 1) {
        // Initialize with SMA
        initialAccumulator += v;
        const initialValue = initialAccumulator / windowSize;
        previous = initialValue;
        return initialValue;
      } else {
        // Apply Welles Wilder smoothing
        // SMMA = previous + (price - previous) / N
        // Same as: SMMA = (previous * (N-1) + price) / N
        const nextValue = v * alpha + (1 - alpha) * previous;
        previous = nextValue;
        return nextValue;
      }
    });
  }

  calculator.undefinedLength = function () {
    const { windowSize } = options;
    return windowSize - 1;
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
